import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PortalLayout from '@/components/layouts/PortalLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { portalAPI } from '@/lib/api';
import { formatDate, getStatusColor, getJobTypeColor } from '@/lib/utils';
import { Loader2, MapPin, Calendar, Package, Plus } from 'lucide-react';

export default function MyRentals() {
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Get demo customer
      const demoResponse = await portalAPI.getDemoCustomer();
      if (demoResponse.data) {
        setCustomer(demoResponse.data);
        
        // Get rentals for this customer
        const rentalsResponse = await portalAPI.getMyRentals(demoResponse.data.id);
        setJobs(rentalsResponse.data.active_jobs || []);
      }
    } catch (error) {
      console.error('Error fetching rentals:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#194461]" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">My Rentals</h1>
            {customer && (
              <p className="text-slate-600 mt-1">
                Viewing rentals for <span className="font-medium">{customer.name}</span>
              </p>
            )}
          </div>
          <Link to="/portal/request-service">
            <Button className="bg-[#194461] hover:bg-[#091D2E]" data-testid="request-service-btn">
              <Plus size={18} className="mr-2" />
              Request Extra Service
            </Button>
          </Link>
        </div>

        {jobs.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="text-slate-400" size={32} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No Active Rentals</h3>
              <p className="text-slate-600 mb-6">
                You don't have any active rentals at the moment.
              </p>
              <Link to="/portal/request-quote">
                <Button className="bg-[#194461] hover:bg-[#091D2E]">
                  Request a Quote
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <Card key={job.id} className="border-slate-200 hover:border-slate-300 transition-colors" data-testid={`rental-card-${job.id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge className={getJobTypeColor(job.job_type)} variant="outline">
                          {job.job_type}
                        </Badge>
                        <Badge className={getStatusColor(job.status)} variant="outline">
                          {job.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <MapPin size={16} className="text-slate-400" />
                          <span>{job.site_address}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar size={16} className="text-slate-400" />
                          <span>Scheduled: {formatDate(job.scheduled_date)}</span>
                          {job.scheduled_time_window && (
                            <span className="text-slate-500">• {job.scheduled_time_window}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <Package size={16} className="text-slate-400" />
                          <span>{job.units_requested} {job.unit_type} unit{job.units_requested > 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      
                      {job.notes && (
                        <p className="mt-3 text-sm text-slate-500 bg-slate-50 p-3 rounded-lg">
                          {job.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Info box */}
        <Card className="mt-8 border-[#52B8E1]/30 bg-[#52B8E1]/5">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-2">Need Changes?</h3>
            <p className="text-sm text-slate-600 mb-4">
              If you need to modify your rental, request extra service, or have questions about your account, please contact us.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/portal/request-service">
                <Button variant="outline" size="sm">
                  Request Extra Service
                </Button>
              </Link>
              <a href="tel:4235550100">
                <Button variant="ghost" size="sm">
                  Call (423) 555-0100
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
